import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  message,
  Spin,
  Typography,
  Upload,
} from 'antd';
import { FilePdfOutlined, FileWordOutlined, InboxOutlined } from '@ant-design/icons';
import {
  deleteArchivoDeInfracciones,
  getArchivosDeInfracciones,
  postArchivoDeInfracciones,
} from 'api/recaudacion/archivosDeInfracciones';
import Previewer from './Previewer';

export const permissionConfig = {
  title: 'Evidencias de infracciones',
  model: 'archivodeinfraccion',
};

const { Dragger } = Upload;

const DEFAULT_MESSAGE = 'Subiendo archivos...';

const EvidenciasDeInfracciones = ({
  infraccion,
  permission,
  mutable,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [onDropEvent, setOnDropEvent] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(DEFAULT_MESSAGE);
  const [fileUrl, setFileUrl] = useState('');

  const fetchData = async () => {
    const response = await getArchivosDeInfracciones({ infraccion });
    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    if (infraccion) {
      fetchData();
    }
  }, [infraccion]);

  const onDrop = async (e) => {
    setOnDropEvent(true);
    setLoadingMessage(DEFAULT_MESSAGE);
    const { files } = e.dataTransfer;
    setLoading(true);
    const _files = Array.from(files);
    const promises = _files.map((file) => postArchivoDeInfracciones({
      infraccion,
      descripcion: file.name.split('.').shift(),
      extension_del_documento: file.name.split('.').pop(),
      archivo: file,
    }));
    const responses = await Promise.all(promises);

    if (responses.every((response) => response)) {
      await fetchData();
      message.success('Archivos subidos exitosamente');
    } else {
      message.warn('No se pudieron subir los archivos');
    }

    setLoading(false);
    setOnDropEvent(false);
  };

  const onRemove = async (file) => {
    if (!permission.delete) {
      message.warn('No tienes permiso para eliminar archivos');
      return;
    }
    setLoading(true);
    setLoadingMessage('Eliminando archivo...');
    const response = await deleteArchivoDeInfracciones(file.uid);

    if (response) {
      await fetchData();
      message.success('Archivo eliminado exitosamente');
    } else {
      message.warn('No se pudo eliminar el archivo');
    }
    setLoading(false);
  };

  const customRequest = async ({
    method,
    file,
    onSuccess,
    onError,
  }) => {
    setLoading(true);
    setLoadingMessage(DEFAULT_MESSAGE);
    const ext = file.name.split('.').pop();
    if (method === 'post' && !onDropEvent) {
      const response = await postArchivoDeInfracciones({
        infraccion,
        descripcion: file.name.split('.').shift(),
        extension_del_documento: ext,
        archivo: file,
      });

      if (response) {
        onSuccess(response);
        await fetchData();
        message.success('Archivo subido exitosamente');
      } else {
        onError();
        message.warn('No se pudo subir el archivo');
      }
    }
    setLoading(false);
  };

  const onPreview = (file) => {
    setFileUrl(file.url);
  };

  const iconRender = (file) => {
    const { extension_del_documento } = file;
    const ext = extension_del_documento.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png'];
    const isImage = imageExtensions.includes(ext);
    if (isImage) {
      return (
        <img
          src={file.url}
          alt={file.descripcion}
          style={{ width: '100%' }}
        />
      );
    }
    if (ext === 'doc' || ext === 'docx') return (<FileWordOutlined />);
    return (<FilePdfOutlined />);
  };

  return (
    <Spin spinning={loading} tip={loadingMessage}>
      <br />
      <Typography.Title level={4}>Evidencias de infracciones</Typography.Title>
      <Dragger
        name="files"
        accept={['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx']}
        listType="picture"
        multiple
        fileList={permission.view ? data : []}
        onDrop={onDrop}
        onRemove={onRemove}
        customRequest={customRequest}
        disabled={mutable || !permission.add}
        onPreview={onPreview}
        iconRender={iconRender}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        {permission.add && (
          <>
            <p className="ant-upload-text">
              Haz clic o arrastra el archivo a esta área para subirlo
            </p>
            <p className="ant-upload-hint">
              Soporte para carga de un solo archivo o múltiple.
            </p>
          </>
        )}
        {!permission.add && (
          <p className="ant-upload-text">
            No tienes permiso para subir archivos
          </p>
        )}
      </Dragger>
      <Previewer
        visible={!!fileUrl}
        setVisible={() => setFileUrl('')}
        iFrameUrl={fileUrl}
      />
    </Spin>
  );
};

EvidenciasDeInfracciones.propTypes = {
  infraccion: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool,
    view: PropTypes.bool,
    delete: PropTypes.bool,
  }),
  mutable: PropTypes.bool,
};

EvidenciasDeInfracciones.defaultProps = {
  infraccion: '',
  permission: {
    add: false,
    view: false,
    delete: false,
  },
  mutable: false,
};

export default EvidenciasDeInfracciones;
